import {
    Badge,
    Box,
    Table,
    TableCell,
    TableRow } from '@mui/material';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { ChangeEvent, FC, useEffect, useState } from 'react';

// Icons
import { ReactComponent as InProgressIcon } from '../../../../assets/icons/in-progress.svg';
import { ReactComponent as IssuesExistsIcon } from '../../../../assets/icons/in-review.svg';
import { ReactComponent as SuccessIcon } from '../../../../assets/icons/success.svg';
import { useAppDispatch } from '../../../../hooks';
import { markBlameIssuesRead } from '../../../../reducers/history';
import { Label, SearchInputWrapper } from '../../../../shared';
import InformationTooltip from '../../../../shared/informationTooltip';
import SortIcon from '../../../../shared/sortIcon';
import { HistoryElement, HistoryTableProps } from '../../../../types/history';
import { formatDateTime } from '../../../../utils/dateUtils';
import IssuesModal from '../Issues';
import * as S from './styled';

// load dayjs duration and dependent plugin
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);

const TasksTable: FC<HistoryTableProps> = ({
    title,
    subtitle,
    allRows
}) => {
    const [ page, setPage ] = useState<number>(0);
    const [ rowsPerPage, setRowsPerPage ] = useState<number>(10);
    const [ rowsState, setRowsState ] = useState<HistoryElement[]>(allRows);
    const pageStart = page * rowsPerPage;
    const pageEnd = (page + 1) * rowsPerPage;
    const rowsStatePaginated = (rowsState).slice(pageStart, pageEnd);
    const [ issuesIndex, setIssuesIndex ] = useState<number | null>(null);
    const dispatch = useAppDispatch();
        
    const TABLE_HEADER_CELLS = { project: 'Project',
        created: 'Task date',
        name: 'Task name',
        duration: 'Net working time',
        description: 'Description & Issues' };

    const handleChangePage = (_: any, _page: number) => setPage(_page);

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    const [ sortType, setSortType ] = useState<string>('desc');
    const [ sortBy, setSortBy ] = useState<string>('');
    const handleSort = (_sortBy: string) => {
        setSortBy(_sortBy);
        const _sortType = sortType === 'asc' ? 'desc' : 'asc';
        setSortType(_sortType);
        const _rows = JSON.parse(JSON.stringify(rowsState));
        _rows.sort((a: HistoryElement, b: HistoryElement) => {
            const _a = a[_sortBy as keyof HistoryElement];
            const _b = b[_sortBy as keyof HistoryElement];
            if (_a === undefined || _a === null || _b === undefined || _b === null) {
                return 0;
            }
            return _sortType === 'asc' ? (_a > _b ? 1 : -1) : _b > _a ? 1 : -1;
        });
        setRowsState(_rows.sort((a: HistoryElement, b: HistoryElement) => {
            const _a = a[_sortBy as keyof HistoryElement];
            const _b = b[_sortBy as keyof HistoryElement];
            if (_a === undefined || _a === null || _b === undefined || _b === null) {
                return 0;
            }
            if (_sortBy === 'duration') {
                return _sortType === 'asc' ? (Number(_a) > Number(_b) ? 1 : -1) : Number(_b) > Number(_a) ? 1 : -1;
            }
            if (_sortBy === 'description') {
                // Sort logic: 1. Waiting feedback (questInvestigation exists), 2. In progress 3. No feedback (questIterationProcessed=true)
                const getScore = (item: HistoryElement) => {
                    if (item.questBlameIssues) {
                        return 3;
                    }
                    if (item.questIterationProcessed) {
                        return 1;
                    }
                    return 2;
                };

                const scoreA = getScore(a);
                const scoreB = getScore(b);
                 
                if (scoreA !== scoreB) {
                    return _sortType === 'asc' ? (scoreA > scoreB ? 1 : -1) : (scoreB > scoreA ? 1 : -1);
                }
                return 0;
            }
            return _sortType === 'asc' ? (_a > _b ? 1 : -1) : _b > _a ? 1 : -1;
        }));
    };

    const [ search, setSearch ] = useState<string>('');
    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target.value;
        setSearch(input);
        if (input) {
            setRowsState(
                allRows.filter((mod) =>
                    mod.name && mod.name.toLowerCase().includes(input.toLowerCase())
                )
            );
        } else {
            setRowsState(allRows);
        }
    };

    const blameIssuesClicked = (index: number) => {
        setIssuesIndex(index);
        const record = rowsStatePaginated[index];
        if (record && record.questBlameIssues && !record.questBlameIssues.blameRead) {
            dispatch(markBlameIssuesRead(record.questBlameIssues.blameId));
        }
    };

    useEffect(() => {
        setRowsState(allRows);
    }, [ allRows ]);

    return (
        <div>
            <IssuesModal
                open={issuesIndex !== null}
                onClose={() => {
                    setIssuesIndex(null);
                }} 
                description={issuesIndex !== null ? rowsStatePaginated[issuesIndex].description : ''} 
                issuesText={issuesIndex !== null ? rowsStatePaginated[issuesIndex].questBlameIssues?.blameIssues : ''}
                title={issuesIndex !== null ? rowsStatePaginated[issuesIndex].name : ''}
            />
        
            <S.Header>
                <S.Titles>
                    <S.Title>{title}</S.Title>
                    <S.Subtitle>{subtitle}</S.Subtitle>
                </S.Titles>
                <SearchInputWrapper
                    search={search}
                    handleSearch={handleSearch}
                />
            </S.Header>
            <S.Container>
                <Box height={24} />
                <Table stickyHeader>
                    <S.StyledTableHead>
                        <TableRow>
                            {Object.keys(TABLE_HEADER_CELLS).map((th, i) => (
                                <TableCell align="center" key={i}>
                                    <Label onClick={() => handleSort(th)}>
                                        {TABLE_HEADER_CELLS[th]}
                                        {sortBy === th && (
                                            <SortIcon sort={sortType} />
                                        )}
                                    </Label>
                                </TableCell>
                            ))}
                        </TableRow>
                    </S.StyledTableHead>
                    <S.StyledTableBody>
                        {rowsStatePaginated.map((row, i) => (
                            <TableRow key={i}>
                                <S.ProjectCell align="center">
                                    {row.project}
                                </S.ProjectCell>
                                <S.TimeCreatedCell align="center">
                                    {formatDateTime(row.created)}
                                </S.TimeCreatedCell>
                                <S.TaskNameCell align="center">
                                    {row.name}
                                </S.TaskNameCell>
                                <S.NetWorkingTimeCell align="center">
                                    {dayjs.duration(row.duration, 'seconds').humanize()}
                                </S.NetWorkingTimeCell>
                                <S.DescriptionAndIssuesCell align="center">
                                    <S.DescriptionWrapper>
                                        <Box>
                                            {row.questBlameIssues ? (
                                                <InformationTooltip text="Issues exist" placement="top">
                                                    <S.BlameIssuesTooltipContent onClick={() => blameIssuesClicked(i)}>
                                                        <Badge color="error" variant="dot" invisible={row.questBlameIssues.blameRead}>
                                                            <IssuesExistsIcon />
                                                        </Badge>
                                                    </S.BlameIssuesTooltipContent>
                                                </InformationTooltip>
                                            ) : row.questIterationProcessed ? (
                                                <InformationTooltip text="Work finished successfully with no issues" placement="top">
                                                    <S.IssuesStatusContainer>
                                                        <SuccessIcon />
                                                    </S.IssuesStatusContainer>
                                                </InformationTooltip>
                                            ) : (
                                                <InformationTooltip text="Quest iteration is in progress" placement="top">
                                                    <S.IssuesStatusContainer>
                                                        <InProgressIcon />
                                                    </S.IssuesStatusContainer>
                                                </InformationTooltip>
                                            )}
                                        </Box>
                                    </S.DescriptionWrapper>
                                </S.DescriptionAndIssuesCell>
                            </TableRow>
                        ))}
                    </S.StyledTableBody>
                    <S.StyledTableFooter>
                        <TableRow>
                            <S.StyledTablePagination
                                colSpan={6}
                                count={rowsState.length}
                                page={page}
                                rowsPerPageOptions={[ 10, 20, 50, 100 ]}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableRow>
                    </S.StyledTableFooter>
                </Table>
            </S.Container>
        </div>
    );
};

export default TasksTable;
